import { AlmanacFile, HandbookItemDocument, HandbookItemFolder, RootState } from 'types'
import { ActionHandler, ActionKey, MenuItem } from '../../Types'
import { useLoading } from 'hooks/UseLoading'
import { FavoriteService } from 'api/services/FavoriteService'
import { ReactComponent as FilledStarIcon } from 'assets/icons/filledStar.svg'
import { ReactComponent as StarIcon } from 'assets/icons/star16.svg'
import { useDispatch, useSelector } from 'react-redux'
import { find, isEqual } from 'lodash'
import { FavoritableType, Favorite } from 'api/clients/FavoriteClient'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { Project } from 'api/clients/ProjectClient'

interface Favoritable {
  favoritable: MenuItem[]
  handleFavorite: ActionHandler
}

export const useFavoritable = ({
  document,
  folder,
  title,
}: {
  document?: HandbookItemDocument
  folder?: HandbookItemFolder | Project | AlmanacFile | null
  title?: string
}): Favoritable => {
  const dispatch = useDispatch()
  let favoritableObj: { key: string; type: FavoritableType; title?: string }

  if (folder) {
    favoritableObj = {
      type: FavoritableType.Project,
      key: folder?.key || folder?.id?.toString(),
      title: title,
    }
  }

  if (document) {
    favoritableObj = { type: FavoritableType.Article, key: document.key, title: title }
  }

  const favorite = useSelector(
    ({ favorites }: RootState) =>
      find(favorites, { favoritable_id: favoritableObj.key, favoritable_type: favoritableObj.type }) as
        | Favorite
        | undefined,
    isEqual,
  )

  const [createFavorite, createFavoriteLoading] = useLoading(FavoriteService.createFavorite)
  const [deleteFavorite, deleteFavoriteLoading] = useLoading(FavoriteService.deleteFavorite)
  const loading = createFavoriteLoading || deleteFavoriteLoading

  const createFavoritable = () => {
    return [
      {
        key: ActionKey.favorite,
        textValue: favorite ? 'Remove from favorites' : 'Add to favorites',
        Icon: favorite || loading ? FilledStarIcon : StarIcon,
      },
    ]
  }
  const handleFavorite = (key: ActionKey) => {
    if (key !== ActionKey.favorite) return false

    if (favorite) {
      // TODO: Fix this the next time the file is edited.
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      deleteFavorite(favorite!.id, dispatch)
      showToastMessage(
        <span>
          <b>{favoritableObj.title}</b> removed from your favorites.
        </span>,
        'success',
      )
    } else {
      createFavorite(favoritableObj.key, favoritableObj.type, dispatch)

      showToastMessage(
        <span>
          <span role="img" aria-label="celebration horn">
            🎉
          </span>{' '}
          <b>{favoritableObj.title}</b> added to your favorites!
        </span>,
        'success',
      )
    }
    return true
  }

  return {
    favoritable: createFavoritable(),
    handleFavorite: handleFavorite,
  }
}
